import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";

import {
  Alert,
  Avatar,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Link as MuiLink,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";


import { getAuthToken } from "../utils/session";
import {
  ArrowBack as ArrowBackIcon,
  BarChartRounded as BarChartRoundedIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Close as CloseIcon,
  ErrorOutline as ErrorOutlineIcon,
  FactCheckRounded as FactCheckRoundedIcon,
  HomeRounded as HomeRoundedIcon,
  InfoOutlined as InfoOutlinedIcon,
  Lock as LockIcon,
  NavigateNextRounded as NavigateNextRoundedIcon,
  PsychologyRounded as PsychologyRoundedIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon,
  ShieldRounded as ShieldRoundedIcon,
  SmartToy as SmartToyIcon,
  TaskAltRounded as TaskAltRoundedIcon,
  Upgrade as UpgradeIcon,
  ManageSearchRounded as ManageSearchRoundedIcon,
  AutoFixHighRounded as AutoFixHighRoundedIcon,
  DataObjectRounded as DataObjectRoundedIcon,
} from "@mui/icons-material";

import DetallePorProceso from "../components/DetallePorProceso";
import axiosInstance from "../axiosInstance";
import { API_ENDPOINTS } from "../config";

const FONT_FAMILY =
  'Inter, Manrope, "SF Pro Display", "Segoe UI", Roboto, Arial, sans-serif';

const COLORS = {
  page: "#F7F9FC",
  surface: "#FFFFFF",
  surfaceSoft: "#FBFCFE",

  border: "rgba(15,36,56,0.10)",
  borderSoft: "rgba(15,36,56,0.07)",
  borderStrong: "rgba(15,36,56,0.16)",

  text: "#172033",
  textStrong: "#102840",
  muted: "#64748B",

  primary: "#1E4B81",
  primaryDark: "#102840",
  primarySoft: "#EEF5FF",

  accent: "#D37D36",
  accentDark: "#B6632C",
  accentSoft: "#FFF4EA",

  success: "#14A67A",
  successSoft: "#EAFBF6",

  danger: "#D32F2F",
  dangerSoft: "#FFEBEE",

  warning: "#C47A1F",
  warningSoft: "#FFF7E8",

  purple: "#5B5CE2",
  purpleSoft: "#F0F0FF",

  shadow: "0 18px 48px rgba(15,36,56,0.06)",
  hoverShadow: "0 18px 42px rgba(15,36,56,0.08)",

  background:
    "radial-gradient(circle at 18% 0%, rgba(30,75,129,0.08), transparent 32%), radial-gradient(circle at 92% 8%, rgba(211,125,54,0.08), transparent 28%), linear-gradient(180deg, #F7F9FC 0%, #FFFFFF 48%, #F7F9FC 100%)",
};

const TYPO = {
  pageTitle: 900,
  sectionTitle: 820,
  bodyStrong: 680,
  body: 520,
  meta: 650,
  chip: 800,
  button: 820,
};

const tipoColores = {
  Gobierno: COLORS.primary,
  Gestion: COLORS.accent,
  Gestión: COLORS.accent,
  Calidad: COLORS.success,
};

const LEVEL_COLORS = {
  N: "#C70000",
  P: "#D14A3D",
  A: "#E1892B",
  T: "#4F6A2C",
};

const panelSx = {
  bgcolor: COLORS.surface,
  border: `1px solid ${COLORS.border}`,
  borderRadius: "22px",
  boxShadow: COLORS.shadow,
};

const primaryButtonSx = {
  minHeight: 42,
  borderRadius: "14px",
  px: 2.2,
  textTransform: "none",
  fontWeight: TYPO.button,
  fontSize: "0.76rem",
  fontFamily: FONT_FAMILY,
  color: COLORS.surface,
  background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})`,
  boxShadow: "0 14px 32px rgba(30,75,129,0.20)",

  "&:hover": {
    background: `linear-gradient(135deg, ${COLORS.primaryDark}, #0B1F33)`,
    boxShadow: "0 18px 40px rgba(30,75,129,0.26)",
  },
};

const secondaryButtonSx = {
  minHeight: 40,
  borderRadius: "13px",
  px: 1.7,
  textTransform: "none",
  fontWeight: TYPO.button,
  fontSize: "0.76rem",
  fontFamily: FONT_FAMILY,
  color: COLORS.primary,
  borderColor: "rgba(30,75,129,0.22)",
  bgcolor: COLORS.surface,

  "&:hover": {
    borderColor: COLORS.primary,
    bgcolor: COLORS.primarySoft,
  },
};

const tableHeadSx = {
  color: COLORS.primary,
  fontWeight: TYPO.sectionTitle,
  fontSize: "0.72rem",
  fontFamily: FONT_FAMILY,
  whiteSpace: "nowrap",
};

const getLevelStatus = (averagePercentage = 0) => {
  const value = Number(averagePercentage) || 0;
  const percentageText = value.toFixed(2);

  if (value <= 15) {
    return {
      text: `${percentageText}% - (N)`,
      color: LEVEL_COLORS.N,
      bg: COLORS.dangerSoft,
    };
  }

  if (value > 15 && value <= 50) {
    return {
      text: `${percentageText}% - (P)`,
      color: LEVEL_COLORS.P,
      bg: COLORS.dangerSoft,
    };
  }

  if (value > 50 && value <= 85) {
    return {
      text: `${percentageText}% - (A)`,
      color: LEVEL_COLORS.A,
      bg: COLORS.warningSoft,
    };
  }

  return {
    text: `${percentageText}% - (T)`,
    color: LEVEL_COLORS.T,
    bg: COLORS.successSoft,
  };
};

const parseAiOverview = (text) => {
  if (!text || typeof text !== "string") {
    return { summary: "", strengths: [], weaknesses: [] };
  }

  const cleaned = text
    .replace(/```json\s*|```text\s*|```\s*/g, "")
    .replace(/\r/g, "")
    .trim();

  const getSection = (labelRegex, nextRegexes = []) => {
    const match = cleaned.match(labelRegex);
    if (!match) return "";
    const start = (match.index || 0) + match[0].length;
    let end = cleaned.length;

    nextRegexes.forEach((rx) => {
      const next = cleaned.slice(start).match(rx);
      if (next && typeof next.index === "number") {
        end = Math.min(end, start + next.index);
      }
    });

    return cleaned.slice(start, end).trim();
  };

  const resumenLabel = /Resumen\s+Ejecutivo\s*:/i;
  const fortalezasLabel = /Fortalezas\s+(?:Clave|identificadas|a\s+Destacar)\s*:/i;
  const debilidadesLabel = /Debilidades\s+(?:Cr[ií]ticas|criticas|a\s+Mejorar)\s*:/i;

  let summary = getSection(resumenLabel, [fortalezasLabel, debilidadesLabel]);
  let strengthsText = getSection(fortalezasLabel, [debilidadesLabel]);
  let weaknessesText = getSection(debilidadesLabel, []);

  if (!summary) {
    const firstCut = cleaned.search(fortalezasLabel);
    summary = firstCut >= 0 ? cleaned.slice(0, firstCut).trim() : cleaned;
    summary = summary.replace(resumenLabel, "").trim();
  }

  const processListText = (listText) => {
    if (!listText) return [];

    const normalized = listText
      .replace(/\n\s*[-*•]\s*/g, "\n- ")
      .replace(/\s+[-*•]\s+/g, "\n- ")
      .replace(/\n\s*\d+[.)]\s*/g, "\n- ");

    return normalized
      .split(/\n+/)
      .map((item) => item.trim().replace(/^[-*•]\s*/, ""))
      .filter((item) => item.length > 0 && !resumenLabel.test(item) && !fortalezasLabel.test(item) && !debilidadesLabel.test(item))
      .slice(0, 5);
  };

  return {
    summary: summary.replace(resumenLabel, "").trim(),
    strengths: processListText(strengthsText),
    weaknesses: processListText(weaknessesText),
  };
};

const buildResultadosGenerales = (preguntasGenerales) => {
  const generalResultsData = [];

  if (preguntasGenerales && typeof preguntasGenerales === "object") {
    Object.values(preguntasGenerales).forEach((nivel) => {
      if (nivel?.preguntas?.length > 0) {
        generalResultsData.push(...nivel.preguntas);
      }
    });
  }

  return generalResultsData;
};

const DetalleEvaluacionPage = () => {
  const { cuestionarioId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [resultados, setResultados] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [openInfoDialog, setOpenInfoDialog] = useState(false);

  const [aiOverview, setAiOverview] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);

  useEffect(() => {
    document.body.style.background = COLORS.page;
  }, []);

  useEffect(() => {
    const token = getAuthToken();

    if (!token) {
      navigate("/login");
      return;
    }

    let userRoleDecoded = null;

    try {
      const decodedToken = jwtDecode(token);
      userRoleDecoded = decodedToken.role;
      setUserRole(userRoleDecoded);
    } catch (error) {
      navigate("/login");
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setAiOverview(null);
      setAiError(null);

      try {
        const response = await axiosInstance.get(
          API_ENDPOINTS.resultadosDetallados(cuestionarioId),
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const resultadosData = response.data;
        const resultadosGenerales = buildResultadosGenerales(
          resultadosData.preguntasGenerales
        );

        setResultados({
          ...resultadosData,
          resultadosGenerales,
        });

        if (userRoleDecoded === "ROLE_PREMIUMPRO") {
          setAiLoading(true);

          try {
            const aiResponse = await axiosInstance.post(
              API_ENDPOINTS.generarResumenEjecutivo(cuestionarioId),
              {},
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            );

            setAiOverview(aiResponse.data.overviewText || aiResponse.data.overview || aiResponse.data.aiOverview || aiResponse.data.resumenEjecutivo || "");
          } catch (aiErr) {
            console.error("Error al obtener la overview de IA:", aiErr);
            setAiError("No se pudo generar el resumen por IA.");
          } finally {
            setAiLoading(false);
          }
        }
      } catch (err) {
        console.error("Error al cargar los resultados detallados:", err);
        setResultados(null);
        setAiError("Error al cargar los datos de la autoevaluación.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [cuestionarioId, navigate]);

  const parsedOverview = useMemo(
    () =>
      aiOverview
        ? parseAiOverview(aiOverview)
        : {
            summary: "",
            strengths: [],
            weaknesses: [],
          },
    [aiOverview]
  );

  const resumenMetricas = useMemo(() => {
    const procesos = resultados?.resultadosPorProceso || [];

    const porTipo = procesos.reduce((acc, proceso) => {
      const tipo = proceso.tipo || "Otros";
      acc[tipo] = (acc[tipo] || 0) + 1;
      return acc;
    }, {});

    return {
      total: procesos.length,
      gobierno: porTipo.Gobierno || 0,
      gestion: porTipo.Gestion || porTipo.Gestión || 0,
      calidad: porTipo.Calidad || 0,
    };
  }, [resultados]);

  if (loading) {
    return <DetalleEvaluacionSkeleton />;
  }

  if (
    !resultados ||
    !resultados.resultadosPorProceso ||
    resultados.resultadosPorProceso.length === 0
  ) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background: COLORS.background,
          display: "grid",
          placeItems: "center",
          p: 3,
          fontFamily: FONT_FAMILY,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            ...panelSx,
            p: 4,
            maxWidth: 620,
            textAlign: "center",
          }}
        >
          <Avatar
            sx={{
              width: 56,
              height: 56,
              mx: "auto",
              mb: 1.5,
              bgcolor: COLORS.dangerSoft,
              color: COLORS.danger,
            }}
          >
            <ErrorOutlineIcon />
          </Avatar>

          <Typography
            sx={{
              color: COLORS.textStrong,
              fontWeight: TYPO.pageTitle,
              fontSize: "1.2rem",
              mb: 0.7,
              fontFamily: FONT_FAMILY,
            }}
          >
            No se pudieron cargar los resultados
          </Typography>

          <Typography
            sx={{
              color: COLORS.muted,
              fontSize: "0.82rem",
              lineHeight: 1.6,
              mb: 2,
              fontFamily: FONT_FAMILY,
            }}
          >
            No hay procesos evaluados para este cuestionario o la información no está disponible.
          </Typography>

          <Button variant="contained" onClick={() => navigate(-1)} sx={primaryButtonSx}>
            Volver
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: COLORS.background,
        pt: { xs: 8.5, md: 2.5 },
        pb: 6,
        fontFamily: FONT_FAMILY,
        color: COLORS.text,
      }}
    >
      <Box sx={{ px: { xs: 1.25, md: 2 }, maxWidth: "100%", mx: "auto" }}>
        <TopBar onBack={() => navigate(-1)} />

        <HeroSection resumenMetricas={resumenMetricas} />

        <Grid container spacing={1.5} sx={{ mb: 2.4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Procesos evaluados"
              value={resumenMetricas.total}
              icon={<FactCheckRoundedIcon />}
              color={COLORS.primary}
              bg={COLORS.primarySoft}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Gobierno del Dato"
              value={resumenMetricas.gobierno}
              icon={<ShieldRoundedIcon />}
              color={COLORS.primary}
              bg={COLORS.primarySoft}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Gestión del Dato"
              value={resumenMetricas.gestion}
              icon={<DataObjectRoundedIcon />}
              color={COLORS.accent}
              bg={COLORS.accentSoft}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Calidad del Dato"
              value={resumenMetricas.calidad}
              icon={<TaskAltRoundedIcon />}
              color={COLORS.success}
              bg={COLORS.successSoft}
            />
          </Grid>
        </Grid>

        <AiOverviewSection
          userRole={userRole}
          aiLoading={aiLoading}
          aiError={aiError}
          aiOverview={aiOverview}
          parsedOverview={parsedOverview}
          onUpgrade={() => navigate("/upgrade-plan")}
        />

        <CapacityMatrixSection
          resultados={resultados}
          openInfoDialog={openInfoDialog}
          onOpenInfoDialog={() => setOpenInfoDialog(true)}
          onCloseInfoDialog={() => setOpenInfoDialog(false)}
        />

        <DetalleProcesosSection
          userRole={userRole}
          resultadosPorProceso={resultados.resultadosPorProceso || []}
          resultadosGenerales={resultados.resultadosGenerales || []}
        />
      </Box>
    </Box>
  );
};

const TopBar = ({ onBack }) => (
  <Paper
    elevation={0}
    sx={{
      ...panelSx,
      px: { xs: 1.5, md: 2 },
      py: 1.25,
      mb: 2,
      position: "sticky",
      top: { xs: 72, md: 12 },
      zIndex: 20,
      bgcolor: "rgba(255,255,255,0.92)",
      backdropFilter: "blur(18px)",
    }}
  >
    <Stack
      direction={{ xs: "column", md: "row" }}
      alignItems={{ xs: "stretch", md: "center" }}
      justifyContent="space-between"
      spacing={1.2}
    >
      <Button
        startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
        onClick={onBack}
        sx={secondaryButtonSx}
      >
        Volver a resultados
      </Button>

      <Breadcrumbs
        separator={<NavigateNextRoundedIcon fontSize="small" sx={{ color: COLORS.muted }} />}
        aria-label="breadcrumb"
      >
        <MuiLink
          underline="hover"
          sx={{
            display: "flex",
            alignItems: "center",
            color: COLORS.primary,
            fontWeight: TYPO.meta,
            fontSize: "0.76rem",
            fontFamily: FONT_FAMILY,
            cursor: "pointer",
          }}
          onClick={onBack}
        >
          <HomeRoundedIcon fontSize="small" sx={{ mr: 0.5 }} />
          Resultados
        </MuiLink>

        <Typography
          sx={{
            color: COLORS.muted,
            fontWeight: TYPO.meta,
            fontSize: "0.76rem",
            fontFamily: FONT_FAMILY,
          }}
        >
          Evaluación detallada
        </Typography>
      </Breadcrumbs>

      <Chip
        icon={<BarChartRoundedIcon sx={{ fontSize: 16 }} />}
        label="Detalle técnico"
        size="small"
        sx={{
          height: 26,
          bgcolor: COLORS.primarySoft,
          color: COLORS.primary,
          border: `1px solid ${COLORS.primary}22`,
          fontWeight: TYPO.chip,
          fontSize: "0.68rem",
          fontFamily: FONT_FAMILY,
          alignSelf: { xs: "flex-start", md: "center" },
        }}
      />
    </Stack>
  </Paper>
);

const HeroSection = ({ resumenMetricas }) => (
  <Paper
    elevation={0}
    sx={{
      ...panelSx,
      p: { xs: 1.8, md: 2.5 },
      mb: 2.3,
      position: "relative",
      overflow: "hidden",
      background:
        "radial-gradient(circle at top right, rgba(211,125,54,0.11), transparent 32%), linear-gradient(135deg, #FFFFFF 0%, #F8FBFF 100%)",
    }}
  >
    <Grid container spacing={2.2} alignItems="center">
      <Grid item xs={12} md={8}>
        <Chip
          label="UNE 0080"
          size="small"
          sx={{
            height: 24,
            mb: 1,
            bgcolor: COLORS.primarySoft,
            color: COLORS.primary,
            border: `1px solid ${COLORS.primary}22`,
            fontWeight: TYPO.chip,
            fontSize: "0.68rem",
            fontFamily: FONT_FAMILY,
          }}
        />

        <Typography
          sx={{
            color: COLORS.textStrong,
            fontWeight: TYPO.pageTitle,
            fontSize: { xs: "1.45rem", md: "1.95rem" },
            letterSpacing: "-0.055em",
            lineHeight: 1.08,
            fontFamily: FONT_FAMILY,
          }}
        >
          Evaluación conforme a UNE 0080
        </Typography>

        <Typography
          sx={{
            color: COLORS.muted,
            fontWeight: TYPO.body,
            fontSize: { xs: "0.8rem", md: "0.88rem" },
            lineHeight: 1.65,
            mt: 0.9,
            maxWidth: 840,
            fontFamily: FONT_FAMILY,
          }}
        >
          Revisión de los niveles alcanzados por proceso y del grado de cumplimiento de los atributos evaluados. La capacidad se interpreta por niveles y evidencias, no mediante una media agregada.
        </Typography>
      </Grid>

      <Grid item xs={12} md={4}>
        <Paper
          elevation={0}
          sx={{
            p: 1.6,
            borderRadius: "20px",
            bgcolor: COLORS.surface,
            border: `1px solid ${COLORS.border}`,
          }}
        >
          <Stack spacing={1.1}>
            <HeroPoint
              icon={<FactCheckRoundedIcon />}
              title={`${resumenMetricas.total} procesos evaluados`}
              text="Procesos incluidos en el alcance de la autoevaluación."
            />

            <HeroPoint
              icon={<ManageSearchRoundedIcon />}
              title="Capacidad por proceso"
              text="Cada proceso se evalúa por nivel y grado de logro de sus atributos."
            />

            <HeroPoint
              icon={<PsychologyRoundedIcon />}
              title="Análisis estratégico"
              text="Disponible con resumen IA en planes superiores."
            />
          </Stack>
        </Paper>
      </Grid>
    </Grid>
  </Paper>
);

const MetricCard = ({ title, value, icon, color, bg }) => (
  <Paper
    elevation={0}
    sx={{
      ...panelSx,
      p: 1.6,
      height: "100%",
      transition: "all 180ms ease",

      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: COLORS.hoverShadow,
        borderColor: `${color}44`,
      },
    }}
  >
    <Stack direction="row" spacing={1.2} alignItems="center">
      <Avatar
        sx={{
          width: 40,
          height: 40,
          bgcolor: bg,
          color,
          border: `1px solid ${color}22`,
        }}
      >
        {icon}
      </Avatar>

      <Box sx={{ minWidth: 0 }}>
        <Typography
          noWrap
          sx={{
            color,
            fontSize: "1.35rem",
            fontWeight: TYPO.pageTitle,
            lineHeight: 1,
            letterSpacing: "-0.04em",
            fontFamily: FONT_FAMILY,
          }}
        >
          {value}
        </Typography>

        <Typography
          noWrap
          sx={{
            color: COLORS.muted,
            fontSize: "0.7rem",
            fontWeight: TYPO.meta,
            fontFamily: FONT_FAMILY,
          }}
        >
          {title}
        </Typography>
      </Box>
    </Stack>
  </Paper>
);

const AiOverviewSection = ({
  userRole,
  aiLoading,
  aiError,
  aiOverview,
  parsedOverview,
  onUpgrade,
}) => (
  <Box sx={{ mb: 2.4 }}>
    {userRole === "ROLE_PREMIUMPRO" ? (
      <Paper
        elevation={0}
        sx={{
          ...panelSx,
          p: 0,
          overflow: "hidden",
          border: `1px solid rgba(30,75,129,0.16)`,
        }}
      >
        <Box
          sx={{
            p: { xs: 1.8, md: 2.3 },
            background:
              "radial-gradient(circle at top right, rgba(30,75,129,0.10), transparent 34%), linear-gradient(135deg, #FFFFFF 0%, #F8FBFF 100%)",
            borderBottom: `1px solid ${COLORS.border}`,
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1.2} alignItems="center">
              <Avatar
                sx={{
                  bgcolor: COLORS.primarySoft,
                  color: COLORS.primary,
                  width: 44,
                  height: 44,
                  border: `1px solid ${COLORS.primary}22`,
                }}
              >
                <SmartToyIcon />
              </Avatar>

              <Box>
                <Typography
                  sx={{
                    color: COLORS.textStrong,
                    fontWeight: TYPO.pageTitle,
                    fontSize: { xs: "1rem", md: "1.12rem" },
                    letterSpacing: "-0.03em",
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  Resumen ejecutivo As-Is
                </Typography>

                <Typography
                  sx={{
                    color: COLORS.muted,
                    fontSize: "0.76rem",
                    lineHeight: 1.5,
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  Lectura estratégica generada a partir de los resultados de la autoevaluación.
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={0.8} alignItems="center">
              <Chip
                icon={<AutoFixHighRoundedIcon sx={{ fontSize: 15 }} />}
                label="Generado por IA"
                size="small"
                sx={{
                  height: 25,
                  bgcolor: COLORS.primarySoft,
                  color: COLORS.primary,
                  border: `1px solid ${COLORS.primary}22`,
                  fontWeight: TYPO.chip,
                  fontSize: "0.66rem",
                  fontFamily: FONT_FAMILY,
                }}
              />

              <Tooltip
                title="El análisis se basa en los datos proporcionados en la autoevaluación. El contenido generado puede contener errores o imprecisiones y debe revisarse antes de usarse en un informe formal."
                placement="top-end"
                arrow
              >
                <IconButton size="small" sx={{ color: COLORS.muted }}>
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Box>

        <Box sx={{ p: { xs: 1.8, md: 2.3 } }}>
          {aiLoading ? (
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.4}
              alignItems="center"
              justifyContent="center"
              sx={{
                minHeight: 150,
                borderRadius: "18px",
                bgcolor: COLORS.surfaceSoft,
                border: `1px solid ${COLORS.border}`,
              }}
            >
              <CircularProgress sx={{ color: COLORS.primary }} />

              <Box>
                <Typography
                  sx={{
                    color: COLORS.textStrong,
                    fontWeight: TYPO.sectionTitle,
                    fontSize: "0.86rem",
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  Analizando resultados
                </Typography>

                <Typography
                  sx={{
                    color: COLORS.muted,
                    fontSize: "0.76rem",
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  La inteligencia artificial está preparando el resumen As-Is.
                </Typography>
              </Box>
            </Stack>
          ) : aiError ? (
            <Alert severity="error" sx={{ borderRadius: "16px" }}>
              {aiError} Por favor, intenta de nuevo más tarde.
            </Alert>
          ) : aiOverview ? (
            <Stack spacing={1.8}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.5, md: 1.8 },
                  borderRadius: "18px",
                  bgcolor: COLORS.surfaceSoft,
                  border: `1px solid ${COLORS.border}`,
                }}
              >
                <Typography
                  sx={{
                    color: COLORS.primary,
                    fontWeight: TYPO.sectionTitle,
                    fontSize: "0.72rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    mb: 0.8,
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  Diagnóstico general
                </Typography>

                <Typography
                  sx={{
                    color: COLORS.text,
                    lineHeight: 1.75,
                    fontSize: "0.82rem",
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  {parsedOverview.summary}
                </Typography>
              </Paper>

              <Grid container spacing={1.6}>
                <Grid item xs={12} md={6}>
                  <InsightListCard
                    type="strength"
                    title="Fortalezas identificadas"
                    subtitle="Aspectos positivos sobre los que apoyar la evolución."
                    items={parsedOverview.strengths}
                    emptyText="No se identificaron fortalezas clave en esta evaluación."
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <InsightListCard
                    type="weakness"
                    title="Debilidades críticas"
                    subtitle="Aspectos prioritarios que requieren atención."
                    items={parsedOverview.weaknesses}
                    emptyText="No se identificaron debilidades clave en esta evaluación."
                  />
                </Grid>
              </Grid>
            </Stack>
          ) : (
            <Typography
              sx={{
                textAlign: "center",
                color: COLORS.muted,
                fontStyle: "italic",
                fontSize: "0.8rem",
                py: 3,
                fontFamily: FONT_FAMILY,
              }}
            >
              Aún no se ha generado la visión general para esta evaluación.
            </Typography>
          )}
        </Box>
      </Paper>
    ) : (
      <Paper
        elevation={0}
        sx={{
          ...panelSx,
          p: { xs: 2.3, md: 3 },
          borderStyle: "dashed",
          textAlign: "center",
          bgcolor: COLORS.surfaceSoft,
        }}
      >
        <Avatar
          sx={{
            width: 58,
            height: 58,
            mx: "auto",
            mb: 1.5,
            bgcolor: COLORS.primarySoft,
            color: COLORS.primary,
          }}
        >
          <LockIcon />
        </Avatar>

        <Typography
          sx={{
            color: COLORS.textStrong,
            fontWeight: TYPO.pageTitle,
            fontSize: "1.12rem",
            mb: 0.6,
            fontFamily: FONT_FAMILY,
          }}
        >
          Resumen ejecutivo As-Is con IA
        </Typography>

        <Typography
          sx={{
            color: COLORS.muted,
            fontSize: "0.82rem",
            lineHeight: 1.6,
            maxWidth: 580,
            mx: "auto",
            mb: 2,
            fontFamily: FONT_FAMILY,
          }}
        >
          Desbloquea un análisis avanzado de tu organización, fortalezas, debilidades críticas y recomendaciones personalizadas generadas por inteligencia artificial.
        </Typography>

        <Button
          variant="contained"
          startIcon={<UpgradeIcon />}
          onClick={onUpgrade}
          sx={{
            ...primaryButtonSx,
            background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.accentDark})`,
          }}
        >
          Actualizar a Premium Pro
        </Button>
      </Paper>
    )}
  </Box>
);

const InsightListCard = ({ type, title, subtitle, items, emptyText }) => {
  const isStrength = type === "strength";
  const color = isStrength ? COLORS.success : COLORS.danger;
  const bg = isStrength ? COLORS.successSoft : COLORS.dangerSoft;
  const Icon = isStrength ? CheckCircleOutlineIcon : ErrorOutlineIcon;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.7,
        borderRadius: "18px",
        border: `1px solid ${color}22`,
        bgcolor: bg,
        height: "100%",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1 }}>
        <Avatar
          sx={{
            width: 34,
            height: 34,
            bgcolor: COLORS.surface,
            color,
            border: `1px solid ${color}22`,
          }}
        >
          <Icon sx={{ fontSize: 19 }} />
        </Avatar>

        <Box>
          <Typography
            sx={{
              color,
              fontWeight: TYPO.sectionTitle,
              fontSize: "0.84rem",
              fontFamily: FONT_FAMILY,
            }}
          >
            {title}
          </Typography>

          {subtitle && (
            <Typography
              sx={{
                color: COLORS.muted,
                fontWeight: TYPO.body,
                fontSize: "0.7rem",
                lineHeight: 1.4,
                fontFamily: FONT_FAMILY,
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </Stack>

      <Divider sx={{ borderColor: `${color}22`, mb: 1 }} />

      <List dense disablePadding>
        {items.length > 0 ? (
          items.map((item, index) => (
            <ListItem key={index} sx={{ py: 0.35, px: 0 }}>
              <ListItemIcon sx={{ minWidth: 25 }}>
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: "8px",
                    display: "grid",
                    placeItems: "center",
                    bgcolor: COLORS.surface,
                    color,
                    fontWeight: TYPO.chip,
                    fontSize: "0.62rem",
                    border: `1px solid ${color}22`,
                  }}
                >
                  {index + 1}
                </Box>
              </ListItemIcon>

              <ListItemText
                primary={
                  <Typography
                    sx={{
                      color: COLORS.text,
                      fontSize: "0.76rem",
                      lineHeight: 1.5,
                      fontFamily: FONT_FAMILY,
                    }}
                  >
                    {item}
                  </Typography>
                }
              />
            </ListItem>
          ))
        ) : (
          <Typography
            sx={{
              color: COLORS.muted,
              fontStyle: "italic",
              fontSize: "0.76rem",
              ml: 0.5,
              fontFamily: FONT_FAMILY,
            }}
          >
            {emptyText}
          </Typography>
        )}
      </List>
    </Paper>
  );
};

const CapacityMatrixSection = ({
  resultados,
  openInfoDialog,
  onOpenInfoDialog,
  onCloseInfoDialog,
}) => {
  const niveles =
    resultados?.resultadosPorProceso?.length > 0
      ? Object.keys(resultados.resultadosPorProceso[0].averageScoresPerLevel || {}).sort()
      : [];

  return (
    <Paper elevation={0} sx={{ ...panelSx, p: { xs: 1.6, md: 2.2 }, mb: 2.4 }}>
      <SectionTitle
        icon={<BarChartRoundedIcon />}
        title="Cálculo de niveles de capacidad"
        description="Los valores muestran el grado de logro de los atributos evaluados por nivel. La capacidad se interpreta conforme a los tramos de logro definidos en UNE 0080."
        action={
          <Button
            variant="outlined"
            onClick={onOpenInfoDialog}
            startIcon={<InfoOutlinedIcon />}
            sx={secondaryButtonSx}
          >
            Ver leyenda
          </Button>
        }
      />

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: "18px",
          border: `1px solid ${COLORS.border}`,
          overflow: "auto",
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: COLORS.surfaceSoft }}>
              <TableCell sx={tableHeadSx}>Proceso</TableCell>

              <TableCell sx={{ ...tableHeadSx, textAlign: "center" }}>
                Nivel de capacidad
              </TableCell>

              {niveles.map((nivel) => (
                <TableCell key={nivel} sx={{ ...tableHeadSx, textAlign: "center" }}>
                  Nivel {nivel === "1" ? "1 específico" : `${nivel} general`}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {resultados.resultadosPorProceso.map((proceso) => (
              <TableRow
                key={proceso.procesoId}
                sx={{
                  "&:nth-of-type(odd)": {
                    bgcolor: "rgba(15,36,56,0.018)",
                  },

                  "&:hover": {
                    bgcolor: COLORS.primarySoft,
                  },
                }}
              >
                <TableCell>
                  <Stack spacing={0.3}>
                    <Typography
                      sx={{
                        color: COLORS.textStrong,
                        fontWeight: TYPO.sectionTitle,
                        fontSize: "0.76rem",
                        fontFamily: FONT_FAMILY,
                      }}
                    >
                      {proceso.nombre}
                    </Typography>

                    {proceso.abreviatura && (
                      <Typography
                        sx={{
                          color: COLORS.muted,
                          fontSize: "0.68rem",
                          fontFamily: FONT_FAMILY,
                        }}
                      >
                        {proceso.abreviatura}
                      </Typography>
                    )}
                  </Stack>
                </TableCell>

                <TableCell sx={{ textAlign: "center" }}>
                  <Chip
                    label={`Nivel ${proceso.nivelCapacidad}`}
                    size="small"
                    sx={{
                      bgcolor: tipoColores[proceso.tipo] || COLORS.primary,
                      color: "#FFFFFF",
                      fontWeight: TYPO.chip,
                      fontSize: "0.66rem",
                      fontFamily: FONT_FAMILY,
                    }}
                  />
                </TableCell>

                {Object.keys(proceso.averageScoresPerLevel || {})
                  .sort()
                  .map((nivel) => {
                    const status = getLevelStatus(proceso.averageScoresPerLevel[nivel]);

                    return (
                      <TableCell key={nivel} sx={{ textAlign: "center" }}>
                        <Chip
                          label={status.text}
                          size="small"
                          sx={{
                            bgcolor: status.bg,
                            color: status.color,
                            border: `1px solid ${status.color}22`,
                            fontWeight: TYPO.chip,
                            fontSize: "0.66rem",
                            fontFamily: FONT_FAMILY,
                          }}
                        />
                      </TableCell>
                    );
                  })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <LegendDialog open={openInfoDialog} onClose={onCloseInfoDialog} />
    </Paper>
  );
};

const DetalleProcesosSection = ({ userRole, resultadosPorProceso, resultadosGenerales }) => (
  <Paper elevation={0} sx={{ ...panelSx, p: { xs: 1.6, md: 2.2 } }}>
    <SectionTitle
      icon={<FactCheckRoundedIcon />}
      title="Detalle por proceso"
      description="Consulta el análisis detallado de los procesos evaluados mediante el componente de detalle integrado en la aplicación."
    />

    <DetallePorProceso
      userRole={userRole}
      resultadosPorProceso={resultadosPorProceso}
      resultadosGenerales={resultadosGenerales}
    />
  </Paper>
);

const SectionTitle = ({ icon, title, description, action }) => (
  <Stack
    direction={{ xs: "column", md: "row" }}
    alignItems={{ xs: "flex-start", md: "center" }}
    justifyContent="space-between"
    spacing={1.4}
    sx={{ mb: 1.7 }}
  >
    <Stack direction="row" spacing={1.1} alignItems="center">
      {icon && (
        <Avatar
          sx={{
            width: 38,
            height: 38,
            bgcolor: COLORS.primarySoft,
            color: COLORS.primary,
            border: `1px solid ${COLORS.primary}22`,
          }}
        >
          {icon}
        </Avatar>
      )}

      <Box>
        <Typography
          sx={{
            color: COLORS.textStrong,
            fontWeight: TYPO.pageTitle,
            fontSize: "1rem",
            letterSpacing: "-0.025em",
            fontFamily: FONT_FAMILY,
          }}
        >
          {title}
        </Typography>

        {description && (
          <Typography
            sx={{
              color: COLORS.muted,
              fontSize: "0.76rem",
              lineHeight: 1.55,
              mt: 0.25,
              fontFamily: FONT_FAMILY,
            }}
          >
            {description}
          </Typography>
        )}
      </Box>
    </Stack>

    {action}
  </Stack>
);

const HeroPoint = ({ icon, title, text }) => (
  <Stack direction="row" spacing={1} alignItems="flex-start">
    <Avatar
      sx={{
        width: 32,
        height: 32,
        bgcolor: COLORS.primarySoft,
        color: COLORS.primary,
        border: `1px solid ${COLORS.primary}22`,

        "& svg": {
          fontSize: 18,
        },
      }}
    >
      {icon}
    </Avatar>

    <Box>
      <Typography
        sx={{
          color: COLORS.textStrong,
          fontWeight: TYPO.sectionTitle,
          fontSize: "0.78rem",
          fontFamily: FONT_FAMILY,
        }}
      >
        {title}
      </Typography>

      <Typography
        sx={{
          color: COLORS.muted,
          fontWeight: TYPO.body,
          fontSize: "0.7rem",
          lineHeight: 1.45,
          fontFamily: FONT_FAMILY,
        }}
      >
        {text}
      </Typography>
    </Box>
  </Stack>
);

const LegendDialog = ({ open, onClose }) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="sm"
    fullWidth
    PaperProps={{
      sx: {
        borderRadius: "24px",
      },
    }}
  >
    <DialogTitle
      sx={{
        p: 2,
        borderBottom: `1px solid ${COLORS.border}`,
        bgcolor: COLORS.surfaceSoft,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" spacing={1} alignItems="center">
          <InfoOutlinedIcon sx={{ color: COLORS.primary }} />

          <Typography
            sx={{
              color: COLORS.textStrong,
              fontWeight: TYPO.sectionTitle,
              fontSize: "0.95rem",
              fontFamily: FONT_FAMILY,
            }}
          >
            Leyenda de niveles de capacidad
          </Typography>
        </Stack>

        <IconButton onClick={onClose} sx={{ color: COLORS.muted }}>
          <CloseIcon />
        </IconButton>
      </Stack>
    </DialogTitle>

    <DialogContent sx={{ p: 2.2 }}>
      <List dense sx={{ py: 0 }}>
        <LegendItem
          icon={<RemoveCircleOutlineIcon />}
          color={LEVEL_COLORS.N}
          letter="N"
          title="No logrado"
          range="≤ 15%"
          text="Hay poca o ninguna evidencia del logro del atributo de proceso definido en el proceso evaluado."
        />

        <LegendItem
          icon={<RemoveCircleOutlineIcon />}
          color={LEVEL_COLORS.P}
          letter="P"
          title="Parcialmente logrado"
          range="> 15% y ≤ 50%"
          text="Hay alguna evidencia de un enfoque y cierto logro del atributo del proceso evaluado."
        />

        <LegendItem
          icon={<CheckCircleOutlineIcon />}
          color={LEVEL_COLORS.A}
          letter="A"
          title="Ampliamente logrado"
          range="> 50% y ≤ 85%"
          text="Hay evidencia de un enfoque sistemático y un logro significativo del atributo de proceso."
        />

        <LegendItem
          icon={<CheckCircleOutlineIcon />}
          color={LEVEL_COLORS.T}
          letter="T"
          title="Totalmente logrado"
          range="> 85%"
          text="Hay evidencia de un enfoque completo y sistemático y pleno logro del atributo evaluado."
        />
      </List>

      <Typography
        sx={{
          mt: 1.4,
          display: "block",
          color: COLORS.muted,
          fontSize: "0.72rem",
          fontStyle: "italic",
          lineHeight: 1.5,
          fontFamily: FONT_FAMILY,
        }}
      >
        Estos criterios se basan en la UNE 0080:2023: Guía de evaluación del Gobierno, Gestión y Gestión de la Calidad del Dato.
      </Typography>
    </DialogContent>
  </Dialog>
);

const LegendItem = ({ icon, color, letter, title, range, text }) => (
  <ListItem sx={{ py: 0.65, px: 0 }}>
    <ListItemIcon sx={{ minWidth: 30, color }}>{icon}</ListItemIcon>

    <ListItemText
      primary={
        <Typography
          sx={{
            color: COLORS.text,
            fontSize: "0.76rem",
            lineHeight: 1.55,
            fontFamily: FONT_FAMILY,
          }}
        >
          <Typography
            component="span"
            sx={{ color, fontWeight: TYPO.pageTitle, mr: 0.5 }}
          >
            {letter}
          </Typography>

          <Typography component="span" sx={{ fontWeight: TYPO.sectionTitle, mr: 0.5 }}>
            — {title} ({range}):
          </Typography>

          {text}
        </Typography>
      }
    />
  </ListItem>
);

const DetalleEvaluacionSkeleton = () => (
  <Box
    sx={{
      minHeight: "100vh",
      background: COLORS.background,
      p: { xs: 2, md: 3 },
      fontFamily: FONT_FAMILY,
    }}
  >
    <Skeleton variant="rounded" height={62} sx={{ borderRadius: 5, mb: 2 }} />
    <Skeleton variant="rounded" height={190} sx={{ borderRadius: 6, mb: 2 }} />

    <Grid container spacing={1.5} sx={{ mb: 2 }}>
      {[...Array(4)].map((_, index) => (
        <Grid item xs={12} sm={6} md={3} key={index}>
          <Skeleton variant="rounded" height={86} sx={{ borderRadius: 5 }} />
        </Grid>
      ))}
    </Grid>

    <Skeleton variant="rounded" height={280} sx={{ borderRadius: 5, mb: 2 }} />
    <Skeleton variant="rounded" height={460} sx={{ borderRadius: 5 }} />
  </Box>
);

export default DetalleEvaluacionPage;
